import type { RoomSettingsDto } from '@/api/dto';

export type RoomKind = 'friends' | 'solo';
export type RoomFormat = 'single' | 'match';

// ── Duration options (A2 design: 60 / 90 / 120 s; default 90) ────────────
export const DURATION_OPTIONS = [60, 90, 120] as const;
export type RoundDuration = (typeof DURATION_OPTIONS)[number];
export const DEFAULT_DURATION: RoundDuration = 90;

// ── Round count options for Match (A2: 3 / 5 / 7; default 5) ─────────────
export const ROUND_COUNT_OPTIONS = [3, 5, 7] as const;
export type RoundCount = (typeof ROUND_COUNT_OPTIONS)[number];
export const DEFAULT_ROUND_COUNT: RoundCount = 5;

// ── Progress visibility (A2: Score = «Видно», Hidden = «Скрыто»)
//    WordCount exists in the server contract but is not exposed in A2 UI.  ─
export const VISIBILITY_OPTIONS = ['Score', 'Hidden'] as const;
export type VisibilityOption = (typeof VISIBILITY_OPTIONS)[number];

// ── mode ↔ (roomKind, format) mapping ────────────────────────────────────

export function deriveMode(
  roomKind: RoomKind,
  format: RoomFormat,
): RoomSettingsDto['mode'] {
  if (roomKind === 'solo') return 'Solo';
  return format === 'match' ? 'Match' : 'SingleRound';
}

export function parseMode(mode: RoomSettingsDto['mode']): { roomKind: RoomKind; format: RoomFormat } {
  return {
    roomKind: mode === 'Solo' ? 'solo' : 'friends',
    format:   mode === 'Match' ? 'match' : 'single',
  };
}

// ── Build full RoomSettingsDto from UI state ──────────────────────────────
// isPublic is not exposed in A2; pass through defaults.
export function buildSettings(
  roomKind: RoomKind,
  format: RoomFormat,
  roundDurationSec: number,
  totalRounds: number,
  progressVisibility: VisibilityOption,
  speedBonus: boolean,
  defaults: Pick<RoomSettingsDto, 'isPublic'>,
): RoomSettingsDto {
  return {
    mode: deriveMode(roomKind, format),
    roundDurationSec,
    totalRounds,
    progressVisibility,
    speedBonus,
    isPublic: defaults.isPublic,
  };
}
